import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { catalogoApi } from '../services/api';
import {
  PageHeader, Loading, EmptyState, Badge, Modal,
} from '../components/ui';
import { formatarMoeda, formatarData } from '../lib/format';
import {
  Plus, Pencil, Trash2, Shirt, Palette, Ruler, Sparkles,
  ShoppingBag, Upload, Store, ExternalLink, Scissors,
} from 'lucide-react';

const abas = [
  { key: 'produtos', label: 'Produtos', icon: Shirt },
  { key: 'estampas', label: 'Estampas', icon: Palette },
  { key: 'cores', label: 'Cores', icon: Sparkles },
  { key: 'tamanhos', label: 'Tamanhos', icon: Ruler },
  { key: 'pedidos', label: 'Pedidos da Loja', icon: ShoppingBag },
] as const;

type Aba = (typeof abas)[number]['key'];

const statusPedidoMap: Record<string, { tom: string; label: string }> = {
  AGUARDANDO_PAGAMENTO: { tom: 'amber', label: 'Aguardando pgto' },
  PAGO: { tom: 'green', label: 'Pago' },
  EM_PRODUCAO: { tom: 'blue', label: 'Em produção' },
  PRONTO: { tom: 'green', label: 'Pronto' },
  ENTREGUE: { tom: 'gray', label: 'Entregue' },
  CANCELADO: { tom: 'red', label: 'Cancelado' },
};

const situacaoProducaoLabel: Record<string, string> = {
  AGUARDANDO_ENTRADA: 'Aguardando',
  EM_PRODUCAO: 'Em produção',
  PRONTO: 'Pronto',
  ENTREGUE: 'Entregue',
  CANCELADO: 'Cancelado',
};

export function CatalogoPage() {
  const [aba, setAba] = useState<Aba>('produtos');

  return (
    <div className="space-y-6">
      <PageHeader
        titulo="Catálogo da Loja"
        descricao="Gerencie o que aparece na loja virtual pública."
      >
        <a href="/loja" target="_blank" rel="noreferrer" className="btn-secondary">
          <Store className="w-4 h-4" />
          Ver loja
          <ExternalLink className="w-3.5 h-3.5" />
        </a>
      </PageHeader>

      {/* Abas */}
      <div className="inline-flex flex-wrap gap-1 bg-white border border-ink-200/70 shadow-card p-1 rounded-xl">
        {abas.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setAba(key)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-150 ${
              aba === key
                ? 'bg-ink-950 text-white shadow-sm'
                : 'text-ink-500 hover:text-ink-800 hover:bg-ink-50'
            }`}
          >
            <Icon className="w-4 h-4" />
            {label}
          </button>
        ))}
      </div>

      {aba === 'produtos' && <AbaProdutos />}
      {aba === 'estampas' && <AbaEstampas />}
      {aba === 'cores' && <AbaCores />}
      {aba === 'tamanhos' && <AbaTamanhos />}
      {aba === 'pedidos' && <AbaPedidosLoja />}
    </div>
  );
}

/* ══ PRODUTOS ═══════════════════════════════════════════ */
function AbaProdutos() {
  const queryClient = useQueryClient();
  const [editando, setEditando] = useState<any>(null);
  const [modalAberto, setModalAberto] = useState(false);

  const { data: produtos, isLoading } = useQuery({
    queryKey: ['catalogo-produtos'],
    queryFn: () => catalogoApi.listarProdutos().then(r => r.data),
  });

  const remover = useMutation({
    mutationFn: (id: string) => catalogoApi.removerProduto(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['catalogo-produtos'] }),
  });

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <button
          onClick={() => { setEditando(null); setModalAberto(true); }}
          className="btn-primary"
        >
          <Plus className="w-4 h-4" /> Novo Produto
        </button>
      </div>

      {isLoading ? (
        <div className="card"><Loading /></div>
      ) : (produtos?.length || 0) === 0 ? (
        <div className="table-shell">
          <EmptyState icon={Shirt} titulo="Nenhum produto" descricao="Cadastre os modelos de camiseta que a loja vende." />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {produtos?.map((p: any) => (
            <div key={p.id} className="card p-0 overflow-hidden">
              <div className="aspect-[16/9] bg-ink-100 relative">
                {p.imagem ? (
                  <img src={p.imagem} alt={p.nome} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Shirt className="w-12 h-12 text-ink-300" />
                  </div>
                )}
                {!p.ativo && (
                  <span className="absolute top-3 left-3 badge bg-ink-900/80 text-white">Inativo</span>
                )}
              </div>
              <div className="p-5">
                <p className="font-display font-semibold text-ink-950">{p.nome}</p>
                {p.descricao && <p className="text-[13px] text-ink-500 mt-1 line-clamp-2">{p.descricao}</p>}
                <div className="flex items-center gap-2 mt-3">
                  <p className="font-bold text-ink-900">
                    {formatarMoeda(p.precoPromocional ?? p.precoBase)}
                  </p>
                  {p.precoPromocional && (
                    <p className="text-xs text-ink-400 line-through">{formatarMoeda(p.precoBase)}</p>
                  )}
                </div>
                <div className="flex gap-2 mt-4">
                  <button
                    onClick={() => { setEditando(p); setModalAberto(true); }}
                    className="btn-secondary flex-1 !py-2 text-[13px]"
                  >
                    <Pencil className="w-3.5 h-3.5" /> Editar
                  </button>
                  <button
                    onClick={() => {
                      if (window.confirm(`Remover "${p.nome}"?`)) remover.mutate(p.id);
                    }}
                    className="btn-secondary !py-2 !px-3 text-[13px] hover:!bg-red-50 hover:!text-red-600 hover:!border-red-200"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <ModalProduto
        aberto={modalAberto}
        produto={editando}
        onFechar={() => setModalAberto(false)}
      />
    </div>
  );
}

function ModalProduto({ aberto, produto, onFechar }: { aberto: boolean; produto: any; onFechar: () => void }) {
  return (
    <Modal
      aberto={aberto}
      onFechar={onFechar}
      titulo={produto ? 'Editar Produto' : 'Novo Produto'}
      descricao="Modelo de peça vendido na loja."
    >
      <FormProdutoInner key={produto?.id || 'novo'} produto={produto} onFechar={onFechar} />
    </Modal>
  );
}

/* Wrapper com estado reiniciado por chave */
function FormProdutoInner({ produto, onFechar }: { produto: any; onFechar: () => void }) {
  const queryClient = useQueryClient();
  const [nome, setNome] = useState(produto?.nome || '');
  const [descricao, setDescricao] = useState(produto?.descricao || '');
  const [precoBase, setPrecoBase] = useState(produto ? String(produto.precoBase) : '');
  const [precoPromocional, setPrecoPromocional] = useState(produto?.precoPromocional ? String(produto.precoPromocional) : '');
  const [imagem, setImagem] = useState<string | null>(produto?.imagem || null);
  const [ativo, setAtivo] = useState(produto?.ativo ?? true);
  const [erro, setErro] = useState('');
  const [carregandoUpload, setCarregandoUpload] = useState(false);

  const salvar = useMutation({
    mutationFn: (data: any) =>
      produto ? catalogoApi.atualizarProduto(produto.id, data) : catalogoApi.criarProduto(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['catalogo-produtos'] });
      queryClient.invalidateQueries({ queryKey: ['loja-catalogo'] });
      onFechar();
    },
    onError: (err: any) => setErro(err.response?.data?.error || 'Erro ao salvar produto.'),
  });

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const arquivo = e.target.files?.[0];
    if (!arquivo) return;
    setCarregandoUpload(true);
    setErro('');
    try {
      const res = await catalogoApi.uploadImagem(arquivo);
      setImagem(res.data.url);
    } catch (err: any) {
      setErro(err.response?.data?.error || 'Falha no upload da imagem.');
    } finally {
      setCarregandoUpload(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!nome.trim() || !precoBase) {
      setErro('Preencha nome e preço base.');
      return;
    }
    salvar.mutate({
      nome: nome.trim(),
      descricao: descricao.trim() || null,
      precoBase: parseFloat(precoBase),
      precoPromocional: precoPromocional ? parseFloat(precoPromocional) : null,
      imagem,
      ativo,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Imagem */}
      <div>
        <label className="input-label">Foto do produto</label>
        <div className="flex items-center gap-4">
          <div className="w-24 h-24 rounded-xl bg-ink-100 overflow-hidden flex items-center justify-center shrink-0">
            {imagem ? (
              <img src={imagem} alt="" className="w-full h-full object-cover" />
            ) : (
              <Shirt className="w-8 h-8 text-ink-300" />
            )}
          </div>
          <label className="btn-secondary cursor-pointer text-[13px]">
            <Upload className="w-4 h-4" />
            {carregandoUpload ? 'Enviando...' : 'Enviar imagem'}
            <input type="file" accept="image/*" className="hidden" onChange={handleUpload} disabled={carregandoUpload} />
          </label>
        </div>
      </div>

      <div>
        <label className="input-label">Nome *</label>
        <input value={nome} onChange={e => setNome(e.target.value)} placeholder="Ex: Camiseta Premium" className="input-field" required />
      </div>
      <div>
        <label className="input-label">Descrição</label>
        <textarea value={descricao} onChange={e => setDescricao(e.target.value)} rows={2} placeholder="Tecido, gramatura, modelagem..." className="input-field" />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="input-label">Preço base *</label>
          <input type="number" step="0.01" min="0.01" value={precoBase} onChange={e => setPrecoBase(e.target.value)} placeholder="0,00" className="input-field" required />
        </div>
        <div>
          <label className="input-label">Preço promocional</label>
          <input type="number" step="0.01" min="0" value={precoPromocional} onChange={e => setPrecoPromocional(e.target.value)} placeholder="Opcional" className="input-field" />
        </div>
      </div>

      <label className="flex items-center gap-2.5 text-sm font-medium text-ink-700 cursor-pointer">
        <input
          type="checkbox"
          checked={ativo}
          onChange={e => setAtivo(e.target.checked)}
          className="w-4 h-4 rounded accent-brand-600"
        />
        Produto ativo na loja
      </label>

      {erro && (
        <div className="p-3.5 bg-red-50 border border-red-200 rounded-xl text-[13px] font-medium text-red-700">{erro}</div>
      )}

      <div className="flex justify-end gap-3 pt-1">
        <button type="button" onClick={onFechar} className="btn-secondary">Cancelar</button>
        <button type="submit" disabled={salvar.isPending} className="btn-primary">
          {salvar.isPending ? 'Salvando...' : 'Salvar Produto'}
        </button>
      </div>
    </form>
  );
}

/* ══ ESTAMPAS ═══════════════════════════════════════════ */
function AbaEstampas() {
  const queryClient = useQueryClient();
  const [editando, setEditando] = useState<any>(null);
  const [modalAberto, setModalAberto] = useState(false);

  const { data: estampas, isLoading } = useQuery({
    queryKey: ['catalogo-estampas'],
    queryFn: () => catalogoApi.listarEstampas().then(r => r.data),
  });

  const remover = useMutation({
    mutationFn: (id: string) => catalogoApi.removerEstampa(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['catalogo-estampas'] }),
  });

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <button onClick={() => { setEditando(null); setModalAberto(true); }} className="btn-primary">
          <Plus className="w-4 h-4" /> Nova Estampa
        </button>
      </div>

      {isLoading ? (
        <div className="card"><Loading /></div>
      ) : (estampas?.length || 0) === 0 ? (
        <div className="table-shell">
          <EmptyState icon={Palette} titulo="Nenhuma estampa" descricao="Cadastre as artes disponíveis para personalização." />
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
          {estampas?.map((e: any) => (
            <div key={e.id} className="card p-0 overflow-hidden">
              <div className="aspect-square bg-ink-100 relative">
                {e.imagem ? (
                  <img src={e.imagem} alt={e.nome} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-brand-100 to-brand-200 flex items-center justify-center p-4">
                    <span className="font-display font-bold text-brand-700 text-center">{e.nome}</span>
                  </div>
                )}
                {!e.ativo && (
                  <span className="absolute top-3 left-3 badge bg-ink-900/80 text-white">Inativa</span>
                )}
              </div>
              <div className="p-4">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-sm font-semibold text-ink-900 truncate">{e.nome}</p>
                  {Number(e.precoAdicional) > 0 && (
                    <p className="text-xs font-bold text-brand-600 whitespace-nowrap">
                      +{formatarMoeda(e.precoAdicional)}
                    </p>
                  )}
                </div>
                {e.categoria && <p className="text-xs text-ink-400 mt-0.5">{e.categoria}</p>}
                <div className="flex gap-2 mt-3">
                  <button
                    onClick={() => { setEditando(e); setModalAberto(true); }}
                    className="btn-secondary flex-1 !py-1.5 text-xs"
                  >
                    <Pencil className="w-3.5 h-3.5" /> Editar
                  </button>
                  <button
                    onClick={() => {
                      if (window.confirm(`Remover a estampa "${e.nome}"?`)) remover.mutate(e.id);
                    }}
                    className="btn-secondary !py-1.5 !px-2.5 text-xs hover:!bg-red-50 hover:!text-red-600 hover:!border-red-200"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <ModalEstampa aberto={modalAberto} estampa={editando} onFechar={() => setModalAberto(false)} />
    </div>
  );
}

function ModalEstampa({ aberto, estampa, onFechar }: { aberto: boolean; estampa: any; onFechar: () => void }) {
  return (
    <Modal
      aberto={aberto}
      onFechar={onFechar}
      titulo={estampa ? 'Editar Estampa' : 'Nova Estampa'}
      descricao="Arte disponível para personalizar as peças."
    >
      <FormEstampaInner key={estampa?.id || 'nova'} estampa={estampa} onFechar={onFechar} />
    </Modal>
  );
}

function FormEstampaInner({ estampa, onFechar }: { estampa: any; onFechar: () => void }) {
  const queryClient = useQueryClient();
  const [nome, setNome] = useState(estampa?.nome || '');
  const [categoria, setCategoria] = useState(estampa?.categoria || '');
  const [precoAdicional, setPrecoAdicional] = useState(estampa?.precoAdicional ? String(estampa.precoAdicional) : '');
  const [imagem, setImagem] = useState<string | null>(estampa?.imagem || null);
  const [ativo, setAtivo] = useState(estampa?.ativo ?? true);
  const [erro, setErro] = useState('');
  const [carregandoUpload, setCarregandoUpload] = useState(false);

  const salvar = useMutation({
    mutationFn: (data: any) =>
      estampa ? catalogoApi.atualizarEstampa(estampa.id, data) : catalogoApi.criarEstampa(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['catalogo-estampas'] });
      queryClient.invalidateQueries({ queryKey: ['loja-catalogo'] });
      onFechar();
    },
    onError: (err: any) => setErro(err.response?.data?.error || 'Erro ao salvar estampa.'),
  });

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const arquivo = e.target.files?.[0];
    if (!arquivo) return;
    setCarregandoUpload(true);
    setErro('');
    try {
      const res = await catalogoApi.uploadImagem(arquivo);
      setImagem(res.data.url);
    } catch (err: any) {
      setErro(err.response?.data?.error || 'Falha no upload da imagem.');
    } finally {
      setCarregandoUpload(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!nome.trim()) { setErro('Informe o nome da estampa.'); return; }
    salvar.mutate({
      nome: nome.trim(),
      categoria: categoria.trim() || null,
      precoAdicional: precoAdicional ? parseFloat(precoAdicional) : 0,
      imagem,
      ativo,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="input-label">Imagem da estampa</label>
        <div className="flex items-center gap-4">
          <div className="w-24 h-24 rounded-xl bg-ink-100 overflow-hidden flex items-center justify-center shrink-0">
            {imagem ? (
              <img src={imagem} alt="" className="w-full h-full object-cover" />
            ) : (
              <Palette className="w-8 h-8 text-ink-300" />
            )}
          </div>
          <label className="btn-secondary cursor-pointer text-[13px]">
            <Upload className="w-4 h-4" />
            {carregandoUpload ? 'Enviando...' : 'Enviar imagem'}
            <input type="file" accept="image/*" className="hidden" onChange={handleUpload} disabled={carregandoUpload} />
          </label>
        </div>
      </div>

      <div>
        <label className="input-label">Nome *</label>
        <input value={nome} onChange={e => setNome(e.target.value)} placeholder="Ex: Leão Geométrico" className="input-field" required />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="input-label">Categoria</label>
          <input value={categoria} onChange={e => setCategoria(e.target.value)} placeholder="Ex: Animais" className="input-field" />
        </div>
        <div>
          <label className="input-label">Valor adicional</label>
          <input type="number" step="0.01" min="0" value={precoAdicional} onChange={e => setPrecoAdicional(e.target.value)} placeholder="0,00" className="input-field" />
        </div>
      </div>

      <label className="flex items-center gap-2.5 text-sm font-medium text-ink-700 cursor-pointer">
        <input type="checkbox" checked={ativo} onChange={e => setAtivo(e.target.checked)} className="w-4 h-4 rounded accent-brand-600" />
        Estampa ativa na loja
      </label>

      {erro && (
        <div className="p-3.5 bg-red-50 border border-red-200 rounded-xl text-[13px] font-medium text-red-700">{erro}</div>
      )}

      <div className="flex justify-end gap-3 pt-1">
        <button type="button" onClick={onFechar} className="btn-secondary">Cancelar</button>
        <button type="submit" disabled={salvar.isPending} className="btn-primary">
          {salvar.isPending ? 'Salvando...' : 'Salvar Estampa'}
        </button>
      </div>
    </form>
  );
}

/* ══ CORES ══════════════════════════════════════════════ */
function AbaCores() {
  const queryClient = useQueryClient();
  const [editando, setEditando] = useState<any>(null);
  const [modalAberto, setModalAberto] = useState(false);

  const { data: cores, isLoading } = useQuery({
    queryKey: ['catalogo-cores'],
    queryFn: () => catalogoApi.listarCores().then(r => r.data),
  });

  const remover = useMutation({
    mutationFn: (id: string) => catalogoApi.removerCor(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['catalogo-cores'] }),
  });

  const alternarAtivo = useMutation({
    mutationFn: ({ id, ativo }: { id: string; ativo: boolean }) => catalogoApi.atualizarCor(id, { ativo }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['catalogo-cores'] }),
  });

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <button onClick={() => { setEditando(null); setModalAberto(true); }} className="btn-primary">
          <Plus className="w-4 h-4" /> Nova Cor
        </button>
      </div>

      <div className="table-shell">
        {isLoading ? (
          <Loading />
        ) : (cores?.length || 0) === 0 ? (
          <EmptyState icon={Sparkles} titulo="Nenhuma cor" descricao="Cadastre as cores de malha disponíveis." />
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Cor</th>
                <th>Código</th>
                <th>Status</th>
                <th className="!text-center">Ações</th>
              </tr>
            </thead>
            <tbody>
              {cores?.map((c: any) => (
                <tr key={c.id}>
                  <td>
                    <div className="flex items-center gap-3">
                      <span
                        className="w-8 h-8 rounded-full ring-1 ring-inset ring-black/10"
                        style={{ backgroundColor: c.hex }}
                      />
                      <span className="font-medium text-ink-800">{c.nome}</span>
                    </div>
                  </td>
                  <td className="text-ink-500 font-mono text-xs">{c.hex}</td>
                  <td>
                    <button onClick={() => alternarAtivo.mutate({ id: c.id, ativo: !c.ativo })}>
                      <Badge tom={c.ativo ? 'green' : 'gray'} label={c.ativo ? 'Ativa' : 'Inativa'} />
                    </button>
                  </td>
                  <td>
                    <div className="flex items-center justify-center gap-1">
                      <button onClick={() => { setEditando(c); setModalAberto(true); }} className="icon-btn" title="Editar">
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => { if (window.confirm(`Remover a cor "${c.nome}"?`)) remover.mutate(c.id); }}
                        className="icon-btn hover:!bg-red-50 hover:!text-red-600"
                        title="Remover"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Modal aberto={modalAberto} onFechar={() => setModalAberto(false)} titulo={editando ? 'Editar Cor' : 'Nova Cor'} largura="max-w-sm">
        <FormCor key={editando?.id || 'nova'} cor={editando} onFechar={() => setModalAberto(false)} />
      </Modal>
    </div>
  );
}

function FormCor({ cor, onFechar }: { cor: any; onFechar: () => void }) {
  const queryClient = useQueryClient();
  const [nome, setNome] = useState(cor?.nome || '');
  const [hex, setHex] = useState(cor?.hex || '#1a1a1a');
  const [erro, setErro] = useState('');

  const salvar = useMutation({
    mutationFn: (data: any) => (cor ? catalogoApi.atualizarCor(cor.id, data) : catalogoApi.criarCor(data)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['catalogo-cores'] });
      queryClient.invalidateQueries({ queryKey: ['loja-catalogo'] });
      onFechar();
    },
    onError: (err: any) => setErro(err.response?.data?.error || 'Erro ao salvar cor.'),
  });

  return (
    <form
      onSubmit={e => {
        e.preventDefault();
        if (!nome.trim()) { setErro('Informe o nome da cor.'); return; }
        salvar.mutate({ nome: nome.trim(), hex });
      }}
      className="space-y-4"
    >
      <div>
        <label className="input-label">Nome *</label>
        <input value={nome} onChange={e => setNome(e.target.value)} placeholder="Ex: Preto" className="input-field" required />
      </div>
      <div>
        <label className="input-label">Cor</label>
        <div className="flex items-center gap-3">
          <input
            type="color"
            value={hex}
            onChange={e => setHex(e.target.value)}
            className="w-12 h-11 rounded-lg border border-ink-200 cursor-pointer p-1"
          />
          <input
            value={hex}
            onChange={e => setHex(e.target.value)}
            className="input-field font-mono uppercase"
            maxLength={7}
          />
        </div>
      </div>
      {erro && <div className="p-3.5 bg-red-50 border border-red-200 rounded-xl text-[13px] font-medium text-red-700">{erro}</div>}
      <div className="flex justify-end gap-3 pt-1">
        <button type="button" onClick={onFechar} className="btn-secondary">Cancelar</button>
        <button type="submit" disabled={salvar.isPending} className="btn-primary">
          {salvar.isPending ? 'Salvando...' : 'Salvar'}
        </button>
      </div>
    </form>
  );
}

/* ══ TAMANHOS ═══════════════════════════════════════════ */
function AbaTamanhos() {
  const queryClient = useQueryClient();
  const [editando, setEditando] = useState<any>(null);
  const [modalAberto, setModalAberto] = useState(false);

  const { data: tamanhos, isLoading } = useQuery({
    queryKey: ['catalogo-tamanhos'],
    queryFn: () => catalogoApi.listarTamanhos().then(r => r.data),
  });

  const remover = useMutation({
    mutationFn: (id: string) => catalogoApi.removerTamanho(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['catalogo-tamanhos'] }),
  });

  const alternarAtivo = useMutation({
    mutationFn: ({ id, ativo }: { id: string; ativo: boolean }) => catalogoApi.atualizarTamanho(id, { ativo }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['catalogo-tamanhos'] }),
  });

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <button onClick={() => { setEditando(null); setModalAberto(true); }} className="btn-primary">
          <Plus className="w-4 h-4" /> Novo Tamanho
        </button>
      </div>

      <div className="table-shell">
        {isLoading ? (
          <Loading />
        ) : (tamanhos?.length || 0) === 0 ? (
          <EmptyState icon={Ruler} titulo="Nenhum tamanho" descricao="Cadastre a grade de tamanhos (P, M, G...)." />
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Tamanho</th>
                <th>Adicional</th>
                <th>Status</th>
                <th className="!text-center">Ações</th>
              </tr>
            </thead>
            <tbody>
              {tamanhos?.map((t: any) => (
                <tr key={t.id}>
                  <td className="font-display font-bold text-ink-900">{t.nome}</td>
                  <td className="text-ink-500">
                    {Number(t.adicional) > 0 ? `+ ${formatarMoeda(t.adicional)}` : '—'}
                  </td>
                  <td>
                    <button onClick={() => alternarAtivo.mutate({ id: t.id, ativo: !t.ativo })}>
                      <Badge tom={t.ativo ? 'green' : 'gray'} label={t.ativo ? 'Ativo' : 'Inativo'} />
                    </button>
                  </td>
                  <td>
                    <div className="flex items-center justify-center gap-1">
                      <button onClick={() => { setEditando(t); setModalAberto(true); }} className="icon-btn" title="Editar">
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => { if (window.confirm(`Remover o tamanho "${t.nome}"?`)) remover.mutate(t.id); }}
                        className="icon-btn hover:!bg-red-50 hover:!text-red-600"
                        title="Remover"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Modal aberto={modalAberto} onFechar={() => setModalAberto(false)} titulo={editando ? 'Editar Tamanho' : 'Novo Tamanho'} largura="max-w-sm">
        <FormTamanho key={editando?.id || 'novo'} tamanho={editando} onFechar={() => setModalAberto(false)} />
      </Modal>
    </div>
  );
}

function FormTamanho({ tamanho, onFechar }: { tamanho: any; onFechar: () => void }) {
  const queryClient = useQueryClient();
  const [nome, setNome] = useState(tamanho?.nome || '');
  const [adicional, setAdicional] = useState(tamanho?.adicional ? String(tamanho.adicional) : '');
  const [erro, setErro] = useState('');

  const salvar = useMutation({
    mutationFn: (data: any) => (tamanho ? catalogoApi.atualizarTamanho(tamanho.id, data) : catalogoApi.criarTamanho(data)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['catalogo-tamanhos'] });
      queryClient.invalidateQueries({ queryKey: ['loja-catalogo'] });
      onFechar();
    },
    onError: (err: any) => setErro(err.response?.data?.error || 'Erro ao salvar tamanho.'),
  });

  return (
    <form
      onSubmit={e => {
        e.preventDefault();
        if (!nome.trim()) { setErro('Informe o tamanho.'); return; }
        salvar.mutate({ nome: nome.trim(), adicional: adicional ? parseFloat(adicional) : 0 });
      }}
      className="space-y-4"
    >
      <div>
        <label className="input-label">Tamanho *</label>
        <input value={nome} onChange={e => setNome(e.target.value)} placeholder="Ex: GG" className="input-field" required />
      </div>
      <div>
        <label className="input-label">Valor adicional</label>
        <input type="number" step="0.01" min="0" value={adicional} onChange={e => setAdicional(e.target.value)} placeholder="0,00" className="input-field" />
        <p className="text-2xs text-ink-400 mt-1.5">Cobrado a mais por peça nesse tamanho (ex: GG +R$ 5).</p>
      </div>
      {erro && <div className="p-3.5 bg-red-50 border border-red-200 rounded-xl text-[13px] font-medium text-red-700">{erro}</div>}
      <div className="flex justify-end gap-3 pt-1">
        <button type="button" onClick={onFechar} className="btn-secondary">Cancelar</button>
        <button type="submit" disabled={salvar.isPending} className="btn-primary">
          {salvar.isPending ? 'Salvando...' : 'Salvar'}
        </button>
      </div>
    </form>
  );
}

/* ══ PEDIDOS DA LOJA ════════════════════════════════════ */
function AbaPedidosLoja() {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['catalogo-pedidos-loja'],
    queryFn: () => catalogoApi.listarPedidos().then(r => r.data),
  });

  const atualizarStatus = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      catalogoApi.atualizarStatusPedido(id, status),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['catalogo-pedidos-loja'] }),
  });

  return (
    <div className="table-shell">
      {isLoading ? (
        <Loading />
      ) : (data?.data?.length || 0) === 0 ? (
        <EmptyState
          icon={ShoppingBag}
          titulo="Nenhum pedido da loja ainda"
          descricao="Os pedidos feitos pelos clientes na loja virtual aparecem aqui."
        />
      ) : (
        <div className="overflow-x-auto">
          <table className="data-table">
            <thead>
              <tr>
                <th>Pedido</th>
                <th>Cliente</th>
                <th>Itens</th>
                <th>Total</th>
                <th>Pagamento</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {data?.data?.map((p: any) => {
                return (
                  <tr key={p.id}>
                    <td>
                      <p className="font-display font-semibold text-ink-900">#{p.numero}</p>
                      <p className="text-xs text-ink-400">{formatarData(p.criadoEm)}</p>
                      {p.pedidoProducao && (
                        <Link
                          to="/pedidos"
                          className="inline-flex items-center gap-1 mt-1 text-2xs font-semibold text-brand-600 hover:text-brand-700"
                          title="Ver no acompanhamento de pedidos"
                        >
                          <Scissors className="w-3 h-3" />
                          Produção #{p.pedidoProducao.numero} · {situacaoProducaoLabel[p.pedidoProducao.situacao] || p.pedidoProducao.situacao}
                        </Link>
                      )}
                    </td>
                    <td>
                      <p className="font-medium text-ink-800">{p.clienteNome}</p>
                      <p className="text-xs text-ink-400">{p.clienteTelefone}</p>
                    </td>
                    <td className="text-ink-500">
                      {p.itens?.reduce((sum: number, i: any) => sum + i.quantidade, 0)} peças
                    </td>
                    <td className="font-semibold text-ink-900">{formatarMoeda(p.valorTotal)}</td>
                    <td>
                      <Badge
                        tom={p.pagamentoStatus === 'APROVADO' ? 'green' : p.pagamentoStatus === 'PENDENTE' ? 'amber' : 'red'}
                        label={
                          p.pagamentoStatus === 'APROVADO' ? 'Aprovado'
                          : p.pagamentoStatus === 'PENDENTE' ? 'Pendente'
                          : p.pagamentoStatus
                        }
                      />
                    </td>
                    <td>
                      <select
                        value={p.status}
                        onChange={e => atualizarStatus.mutate({ id: p.id, status: e.target.value })}
                        className="input-field !py-1.5 !px-2.5 text-xs !w-auto"
                      >
                        {Object.entries(statusPedidoMap).map(([value, { label }]) => (
                          <option key={value} value={value}>{label}</option>
                        ))}
                      </select>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
